import { apiClient } from "@/api/client";
import useGlobalStore from "@/store/globalStore";
import { AxiosError } from "axios";
import { create } from "zustand";

export interface Team {
  id: string;
  name: string;
  team_type: string;
  media_agency_id: string;
  media_agency?: MediaAgency;
}

export interface MediaAgency {
  id: string;
  name: string;
  logo_url?: string;
}

interface TeamsState {
  teams: Array<Team> | null;
  setTeams: (teams: Array<Team>) => void;
  getTeams: () => Promise<void>;
  createTeam: (name: string, team_type: string) => Promise<void>;
  updateTeam: (team: Team) => Promise<void>;
  deleteTeam: (id: string) => Promise<void>;
}

export interface TeamsRespose {
  teams: Array<Team>;
}

// store/teamsStore.ts
const useTeamStore = create<TeamsState>((set, get) => ({
  teams: [],
  setTeams: (teams) => {
    set({ teams });
  },

  getTeams: async () => {
    try {
      const response = await apiClient.get<TeamsRespose>("/teams");
      set({ teams: response.data.teams });
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      throw new Error(
        axiosError.response?.data?.error || "Something went wrong"
      );
    }
  },

  createTeam: async (name: string, team_type: string) => {
    try {
      const response = await apiClient.post("/teams", { name, team_type });
      const newTeam = response.data.team;

      set((state) => ({
        teams: [...(state.teams ?? []), newTeam],
      }));
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      throw new Error(
        axiosError.response?.data?.message || "Something went wrong"
      );
    }
  },

  updateTeam: async (team: Team) => {
    try {
      const response = await apiClient.put(`/teams/${team.id}`, team);
      const updatedTeam = response.data.team;

      set((state) => ({
        teams: (state.teams ?? []).map((t) =>
          t.id === team.id ? updatedTeam : t
        ),
      }));
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      throw new Error(
        axiosError.response?.data?.message || "Something went wrong"
      );
    }
  },

  deleteTeam: async (id: string) => {
    try {
      await apiClient.delete(`/teams/${id}`);
      set((state) => ({
        teams: (state.teams ?? []).filter((team) => team.id !== id),
      }));
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      throw new Error(
        axiosError.response?.data?.message || "Something went wrong"
      );
    }
  },
}));

export default useTeamStore;
